"use client";

import HeightIcon from "@/app/assets/height-icon.svg";
import LengthIcon from "@/app/assets/length-icon.svg";
import PackageIcon from "@/app/assets/package-icon.svg";
import WidthIcon from "@/app/assets/width-icon.svg";
import { FieldLabel, SectionHeader } from "@/components/Atoms/Typography";
import { primaryColor, secondaryColor } from "@/lib/theme";
import { AnimatePresence, motion } from "framer-motion";
import { HelpCircle, X } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect } from "react";
import { createPortal } from "react-dom";

const BoxPreview3D = dynamic<{
  length: string;
  width: string;
  height: string;
  autoRotate?: boolean;
}>(() => import("@/components/Molecules/BoxPreview3D"), { ssr: false });

const SPECS = [
  { icon: PackageIcon, label: "Peso", value: "até 30 kg" },
  { icon: HeightIcon, label: "Altura", value: "até 18 cm" },
  { icon: WidthIcon, label: "Largura", value: "até 28,5 cm" },
  { icon: LengthIcon, label: "Comprimento", value: "até 39 cm" },
];

const isDemo = process.env.NEXT_PUBLIC_DEMO === "true";
// In the demo the secondary color is bright yellow, which makes the white
// dimension icons unreadable. Fall back to the primary color there.
const specIconBg = isDemo ? primaryColor : secondaryColor;

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function PackageInfoModal({ open, onClose }: Props) {
  // Trava a rolagem do fundo enquanto o diálogo está aberto. Os valores
  // anteriores voltam no cleanup em vez de "" — assim um outro overlay que já
  // tivesse travado a página não é destravado por engano ao fechar este.
  useEffect(() => {
    if (!open) return;
    const { documentElement, body } = document;
    const previousHtmlOverflow = documentElement.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    documentElement.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      documentElement.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
    };
  }, [open]);

  if (typeof document === "undefined") return null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="pkg-info-backdrop"
            className="fixed inset-0 z-[10000] bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
            onClick={onClose}
          />
          <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              key="pkg-info-panel"
              // `max-h-full` em vez de `h-full`: o painel tem a altura do
              // próprio conteúdo e só para de crescer na área que sobra do
              // `p-4` do container — o mesmo respiro nos quatro lados. Sem
              // esse teto, o painel passava da tela no celular e o
              // `overflow-y-auto` do meio não tinha limite para rolar.
              className="pointer-events-auto bg-white w-full max-h-full max-w-xl rounded-card flex flex-col overflow-hidden shadow-float"
              initial={{ opacity: 0, scale: 0.88 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.88 }}
              transition={{
                type: "spring",
                damping: 28,
                stiffness: 360,
                mass: 0.85,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <SectionHeader
                className="shrink-0"
                title="Informações do pacote"
                icon={
                  <HelpCircle
                    className="w-5 h-5"
                    style={{ color: primaryColor }}
                  />
                }
                action={
                  <button
                    id="btn-package-info-close"
                    type="button"
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-ground text-slate transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                }
              />

              {/* Sem `flex-1`: o meio só ocupa o que o conteúdo pede. Quando
                  o painel bate no teto, ele é o único que encolhe (cabeçalho e
                  rodapé são `shrink-0`) e passa a rolar. `min-h-0` é o que
                  permite esse encolhimento — sem ele a rolagem não acontece. */}
              <div className="min-h-0 overflow-y-auto px-6 py-5 space-y-3">
                <p className="text-fine text-slate leading-relaxed">
                  O valor do frete pode alterar de acordo com as proporções de
                  cada pacote. <br />
                  Informe ou confirme o peso e as dimensões para obter o frete
                  mais preciso.
                </p>
                {/* Altura livre: com o gizmo empilhado no celular, os 170px
                    fixos de antes cortavam o desenho. */}
                <div className="flex w-full justify-center py-2">
                  <div className="w-full">
                    <BoxPreview3D
                      length="39"
                      width="28.5"
                      height="18"
                      autoRotate
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  {SPECS.map(({ icon, label, value }) => (
                    <div
                      key={label}
                      className="flex items-center gap-3 rounded-control border border-line px-4 py-3"
                    >
                      <div
                        className="w-9 h-9 rounded-chip flex items-center justify-center shrink-0"
                        style={{ backgroundColor: specIconBg }}
                      >
                        <Image
                          src={icon}
                          alt={label}
                          width={18}
                          height={18}
                          className="brightness-0 invert"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <FieldLabel>{label}</FieldLabel>
                        <p className="text-body font-semibold text-ink">
                          {value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-line shrink-0">
                <button
                  id="btn-package-info-confirm"
                  type="button"
                  onClick={onClose}
                  className="w-full h-11 rounded-control text-sm font-bold text-white transition-colors"
                  style={{ backgroundColor: primaryColor }}
                >
                  Entendi
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
